import { z } from "zod";
import {
  isValidPhoneNumber,
  type CountryCode as PhoneCountryCode,
} from "libphonenumber-js";

export {
  AGENT_MODEL_AUTO,
  AGENT_MODEL_CATALOG,
  AGENT_MODEL_PROVIDER_ORDER,
  AGENT_MODEL_RUNTIME_IDS,
  agentModelDescriptionKey,
  agentModelMinimumTier,
  findAgentModel,
  getDefaultAgentModelForPlan,
  isAgentModelSelection,
  isProAgentModel,
  type AgentModelBrand,
  type AgentModelEntry,
  type AgentModelMinimumTier,
  type AgentModelProvider,
  type AgentModelTier,
} from "./agent-models";
export {
  LEGAL_INLINE_MAX_LENGTH,
  findLegalInlineIssues,
  legalBlockSchema,
  legalBlocksToPlainText,
  legalChangeSummarySchema,
  legalDocumentContentSchema,
  legalDocumentToPlainText,
  legalInlineToPlainText,
  legalLeafBlockSchema,
  legalLocalizedContentSchema,
  legalSectionSchema,
  legalSectionToPlainText,
  legalSubsectionBlockSchema,
  parseLegalInline,
  parseLegalInlineLabel,
  type LegalInlineLabelNode,
  type LegalInlineNode,
} from "./legal-blocks";
export {
  subscriptionCatalog,
  PLAN_TIER_ORDER,
  PLAN_SETS,
  PAID_PLAN_TIERS,
  normalizeLegacyPlanTier,
  comparePlanTiers,
  getHighestPlanTier,
  getModelPolicyForTier,
  getPlanTierDefinition,
  getFeatureDefinition,
  getAiActionDefinition,
} from "./subscription/catalog";
export {
  resolveSubscriptionSnapshot,
  evaluateEntitlement,
  type ResolveSubscriptionSnapshotInput,
} from "./subscription/entitlements";
export {
  getRemainingActionsToday,
  resolveMetering,
  type MeteringResolution,
} from "./subscription/metering";
export {
  buildCustomerAllowanceSnapshot,
  getCustomerAllowanceDefinition,
  getCustomerAllowanceWindow,
  resolveCustomerActionAllowance,
  type CustomerAllowanceWindow,
} from "./subscription/allowances";
export {
  selectUpgradePrompt,
  getUpgradePromptByKey,
} from "./subscription/prompts";
export { selectModelPolicy } from "./subscription/routing";
export { mergeSubscriptionCatalogOverride } from "./subscription/overrides";
export {
  revenueCatPackageIds,
  legacyRevenueCatEntitlements,
  legacyRevenueCatProductIds,
  normalizeRevenueCatProductId,
  getRevenueCatPlanMapping,
  listRevenueCatPlanMappings,
  listRevenueCatV2ProductIds,
  isRevenueCatV2ProductId,
  findRevenueCatPlanMappingByProductId,
  getRevenueCatAppUserId,
  supportsNativeRevenueCatPurchase,
  supportsRevenueCatWebCheckout,
  type RevenueCatPlanMapping,
} from "./subscription/revenuecat";
export {
  normalizePublicHandle,
  isPublicHandleLike,
  isReservedPublicSubdomain,
  getPublicRootDomain,
  getPublicHandleFromHostname,
  buildCanonicalProfileUrl,
  buildPersonHubUrl,
  buildPrettyProfileUrl,
  buildPersonCvUrl,
  buildPersonTailoredCvUrl,
  splitCvHandle,
  buildPersonCvLocaleUrl,
  buildCanonicalCvUrl,
  buildPersonCvPathUrl,
  buildPersonResumeJsonUrl,
  buildPersonResumeJsonPathUrl,
  buildPersonPrivateCvShareUrl,
  buildLegacyCvShareUrl,
  buildPublicProfileUrls,
  buildAppJobUrl,
  buildAppJobApplyUrl,
  toCatalogJobRouteId,
  fromCatalogJobRouteId,
  CATALOG_JOB_ROUTE_ID_PATTERN,
  buildCanonicalJobUrl,
  buildPublicJobSlug,
  parsePublicJobSlug,
  buildPublicPath,
  RESERVED_PUBLIC_SUBDOMAINS,
  type ParsedPublicJobSlug,
  type PublicProfileUrlSet,
  type PublicUserUrlSet,
  type PublicCompanyUrlSet,
  type PublicJobSlugInput,
  type SplitCvHandle,
} from "./public-url";
export {
  DEFAULT_CV_CONFIG,
  VALID_CV_ACCENT_COLORS,
  VALID_CV_LAYOUT_STYLES,
  coerceCvConfigForEntitlement,
  normalizeCvConfig,
} from "./cv-config";
export {
  SHARE_CARD_TITLE_MAX,
  SHARE_CARD_DESC_MAX,
  SHARE_CARD_CTA_MAX,
  SHARE_CARD_URL_MAX,
  SHARE_CARD_DEFAULT_DESCRIPTION,
  SHARE_CARD_DEFAULT_CTA,
  SHARE_CARD_DOWNLOAD_CTA,
  SHARE_CARD_FREE_CTAS,
  SHARE_CARD_DEFAULT_BACKGROUND,
  SHARE_CARD_DEFAULT_PATTERN,
  SHARE_CARD_DEFAULT_CUSTOM_COLOR,
  SHARE_CARD_THEME_IDS,
  SHARE_CARD_THEMES,
  SHARE_CARD_THEME_CSS,
  SHARE_CARD_ART_KEYS,
  SHARE_CARD_GRADIENT_THEME_IDS,
  SHARE_CARD_FREE_THEMES,
  SHARE_CARD_PATTERNS,
  SHARE_CARD_FREE_PATTERNS,
  SHARE_CARD_SILHOUETTE_OPACITY,
  SHARE_CARD_GCC_SILHOUETTE_OPACITY,
  SHARE_CARD_BACKGROUNDS,
  LEGACY_SHARE_CARD_GRADIENTS,
  getShareCardTheme,
  resolveShareCardTheme,
  shareCardThemeId,
  shareCardCustomInk,
  hexLuminance,
  isHexDark,
  resolveShareCardBackground,
  resolveShareCardGradient,
  buildCustomBackground,
  isCustomBackground,
  customBackgroundHex,
  normalizeShareCardPattern,
  normalizeShareCardCta,
  isShareCardDownloadCta,
  shareCardCtaPreset,
  resolveShareCardCta,
  isTabbioShareHost,
  parseShareCardUrl,
  normalizeShareCardUrl,
  shareCardLinkDisplay,
  resolveShareCardLink,
  clampShareCardText,
  defaultShareCardConfig,
  normalizeShareCardConfig,
  isFreeShareCardTheme,
  paidShareCardChoices,
  sanitizeShareCardForFree,
  coerceShareCardForEntitlement,
  resolveShareCard,
  shareCardConfigSchema,
  type ShareCardThemeId,
  type ShareCardThemeKind,
  type ShareCardArtKey,
  type ShareCardTheme,
  type ShareCardBackgroundKind,
  type ShareCardBackgroundPreset,
  type ShareCardGradient,
  type ShareCardCtaKind,
  type ShareCardCtaPreset,
  type ShareCardPaidChoice,
  type ResolvedShareCardTheme,
  type ResolvedShareCardCta,
  type ResolvedShareCardLink,
  type ResolvedShareCard,
  type ShareCardConfigInput,
} from "./cv-share-card";
export {
  SHARE_CARD_MARK_ART,
  SHARE_CARD_CV_ART,
  SHARE_CARD_BRIEFCASE_ART,
  SHARE_CARD_WAVE_ART,
  SHARE_CARD_AVATAR_PLACEHOLDER_ART,
  SHARE_CARD_ORBIT_ART,
  SHARE_CARD_BOX,
  SHARE_CARD_BRUSH_ICON,
  SHARE_CARD_CTA_ICON_PATHS,
  SHARE_CARD_SILHOUETTES,
  SHARE_CARD_DESIGN_WIDTH,
  SHARE_CARD_ASPECT,
  SHARE_CARD_RADIUS,
  SHARE_CARD_FLUID,
  SHARE_CARD_FIXED,
  shareCardInkAlpha,
  shareCardFluidCss,
  shareCardMetrics,
  containShareCardArt,
  layoutShareCardSilhouette,
  type ShareCardSvgArt,
  type ShareCardSilhouetteSpec,
  type ShareCardSilhouettePattern,
  type ShareCardSilhouetteLayout,
  type ShareCardFluidSpec,
  type ShareCardMetrics,
} from "./share-card-art";
export {
  PROFILE_VISIBILITY_VALUES,
  PROFILE_PHOTO_VISIBILITY_VALUES,
  PROFILE_CONTACT_VISIBILITY_VALUES,
  DEFAULT_CV_VISIBILITY_DEFAULTS,
  DEFAULT_PROFILE_PRIVACY_SETTINGS,
  PRIVACY_POLICY_VERSION,
  DEFAULT_PUBLIC_PROFILE_PRIVACY,
  normalizeProfileVisibility,
  normalizeProfilePhotoVisibility,
  normalizeProfileContactVisibility,
  normalizeProfileContactVisibilitySettings,
  normalizeCvVisibilityDefaults,
  normalizeProfilePrivacySettings,
  deriveCvVisibilityDefaults,
  getProfileVisibilityPersistence,
  getPublicProfileEffectivePrivacy,
  getPrivateCvVisibilityDefaults,
} from "./profile-privacy";
// public-profile-links is intentionally NOT barrel-exported here, same Node
// ESM submodule resolution constraint as username-policy above. Consumers
// import from '@tabbio/utils/public-profile-links' directly (see the
// package.json exports map).

export {
  MAX_CV_NAME,
  MAX_CV_HEADLINE,
  MAX_CV_BIO,
  MAX_CV_SUMMARY,
  MAX_CV_DESCRIPTION,
  MAX_LINK_URL,
  MAX_CV_SKILL,
  MAX_CV_LANGUAGE,
  MAX_CV_SKILL_CATEGORY,
  MAX_CV_DEGREE,
  MAX_CV_SCHOOL,
  MAX_CV_CERT_NAME,
  MAX_CV_CERT_ISSUER,
  MAX_CV_CERT_URL,
  MIN_EDUCATION_YEAR,
  CV_FIELD_MAX,
  cvHeadlineSchema,
  cvSummarySchema,
  cvDescriptionSchema,
  cvEducationSchema,
  cvCertificationSchema,
  normalizeCredentialUrl,
  cvSkillEntrySchema,
  cvSkillCategorySchema,
  cvLanguageEntrySchema,
  cvHighlightSchema,
  parseCvPickerDate,
  isCvDateInFuture,
  normalizeToYearMonth,
  formatYearMonthForPicker,
  splitCvPeriodRange,
  ONGOING_DATE_PATTERN,
  isOngoingDateMarker,
  SAFE_NAME_PATTERN,
  isValidNameField,
  nameFieldError,
  stripHtmlFromPlainText,
  SUPPORTED_CV_LANGUAGE_NAMES,
  canonicalizeCvLanguageName,
  isSupportedCvLanguageName,
  type SupportedCvLanguageName,
} from "./cv-validation";

// parsed-cv-to-overrides is intentionally NOT barrel-exported here, same
// Node ESM submodule resolution constraint as username-policy above.
// Consumers import from '@tabbio/utils/parsed-cv-to-overrides' directly
// (see package.json exports map).

// extension is intentionally NOT barrel-exported here, same Node ESM submodule
// resolution constraint as username-policy and parsed-cv-to-overrides above.
// Consumers import from '@tabbio/utils/extension' directly (see the
// package.json exports map). Barrel-exporting it makes Jest load the
// extensionless shim untransformed and every suite that touches this barrel
// dies with "Unexpected token 'export'".

export {
  PAGE_CONTEXT_ROUTE_MAX,
  PAGE_CONTEXT_ENTITY_ID_MAX,
  PAGE_CONTEXT_ENTITY_LABEL_MAX,
  PAGE_CONTEXT_STATE_MAX_CHARS,
  PAGE_CONTEXT_SCREENS,
  PAGE_CONTEXT_ENTITY_TYPES,
  pageContextScreenSchema,
  pageContextEntityTypeSchema,
  pageContextEntitySchema,
  pageContextEnvelopeSchema,
  parsePageContextEnvelope,
} from "./page-context";

export { normalizeJobDescriptionMarkdown } from "./job-description";

export {
  SKILL_MANIFEST_SCHEMA_VERSION,
  SKILL_FORMAT_V2,
  CV_SECTION_IDS,
  SKILL_CV_SECTION_PROMPT_MAX,
  skillManifestSchema,
  skillKindSchema,
  skillRunConfigSchema,
  skillCvConfigSchema,
  skillTypeSchema,
  skillCountrySchema,
  skillLevelSchema,
  skillLanguageSchema,
  skillCategorySchema,
  skillRecommendedModelSchema,
  skillContextSourceSchema,
  skillLayoutSchema,
  skillInputFieldSchema,
  cvSectionIdSchema,
  parseSkillManifest,
  extractFrontmatter,
  parseFrontmatterYaml,
  serializeSkillManifestExtras,
  normalizeSkillTier,
  deriveSkillLocales,
  parseSkillIconSpec,
  skillAccentToCvColor,
  skillTemplateToCvStyle,
  type SkillManifest,
  type SkillTier,
  type SkillKind,
  type SkillRunConfig,
  type SkillCvConfig,
  type SkillCvSectionConfig,
  type SkillTypeValue,
  type SkillLayoutConfig,
  type SkillInputFieldConfig,
  type ParsedSkillIcon,
  type CvSectionId,
  type ParseSkillManifestResult,
  type ExtractedFrontmatter,
} from "./skill-manifest";

// email-dedupe is intentionally NOT barrel-exported here, same Node ESM
// submodule resolution constraint as username-policy, parsed-cv-to-overrides and
// extension above. Consumers import from '@tabbio/utils/email-dedupe' directly
// (see the package.json exports map). Barrel-exporting it needs an extensionless
// shim for the API's ESM runtime, and Jest then loads that shim untransformed
// and every suite touching this barrel dies with "Unexpected token 'export'".

export {
  NOVA_PLAN_SCHEMA_VERSION,
  NOVA_LIMITS_BOUNDS,
  NOVA_MAX_STEPS,
  NOVA_READ_ONLY_STEP_TYPES,
  NOVA_CONNECTOR_STEP_TYPE,
  isNovaSideEffectingStep,
  novaPlanDraftSchema,
  novaAutomationSchema,
  novaAutomationJsonSchema,
  enforceNovaSafetyDefaults,
  validateNovaPlan,
  NOVA_REPORT_STEP_TYPES,
  NOVA_REPORT_LIMITS,
  NOVA_REPORT_OUTPUT_TYPE,
  isNovaReportStepType,
  readNovaReportStepParams,
  isNovaPdfLink,
  countNovaReadCalls,
  countNovaResearchCalls,
  type NovaConnectorCatalogEntry,
  type NovaPlanDraftParsed,
  type NovaPlanValidationError,
  type NovaPlanValidationResult,
  type NovaResearchWebParams,
  type NovaReadSourcesParams,
  type NovaComposeReportParams,
  type NovaRenderDocumentParams,
  type NovaDeliverParams,
  type NovaReportStepParams,
} from "./automation/nova-plan";

// Username policy is in a separate file but must be imported directly
// due to Node.js ESM submodule resolution constraints in the monorepo.
// Consumers that need username-policy values should import from
// '@tabbio/utils/username-policy' (see package.json exports map).
// Published site names follow it: import '@tabbio/utils/site-names'.

export const emailSchema = z.string().email("Invalid email address");

export function isValidEmail(input: string): boolean {
  return emailSchema.safeParse(input.trim()).success;
}

// Pragmatic URL check: accepts `https://domain.tld[/path]` and bare `domain.tld[/path]`.
// Existing user data (LinkedIn/GitHub links) often lacks a protocol, so a strict
// `z.string().url()` would reject otherwise-valid handles. Domain must be at least
// `host.tld` with a 2+ char TLD.
//
// Known gaps (acceptable for V1: extend in a follow-up if user data demands it):
// - rejects ports (`example.com:8080`)
// - rejects IDN domains (`münchen.de`)
// - rejects IP literals and `localhost`
// - rejects userinfo (`user@example.com`)
// - rejects whitespace anywhere in path/query
export const URL_REGEX =
  /^(?:https?:\/\/)?(?:[\w-]+\.)+[a-zA-Z]{2,}(?:[/?#][^\s]*)?$/;
export const URL_INVALID_MESSAGE = "Enter a valid URL";

export function isValidUrl(input: string): boolean {
  return URL_REGEX.test(input.trim());
}

export const urlSchema = z
  .string()
  .trim()
  .refine((v) => URL_REGEX.test(v), URL_INVALID_MESSAGE);

export const optionalUrlSchema = z
  .string()
  .trim()
  .optional()
  .refine((v) => !v || URL_REGEX.test(v), URL_INVALID_MESSAGE);

// Returns true when `dateStr` parses to a real Date that is the current month
// or earlier. Anything in a future month is rejected (so users can pick the
// current month for ongoing items, but not "Jan 2050").
export function isDateInFuture(dateStr: string): boolean {
  if (!dateStr) return false;
  const parsed = new Date(dateStr);
  if (Number.isNaN(parsed.getTime())) return false;
  const now = new Date();
  const firstOfNextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return parsed >= firstOfNextMonth;
}

export function isValidDateRange(start: string, end: string): boolean {
  if (!start || !end) return true;
  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return true;
  return e >= s;
}

// Password complexity policy (product decision):
//   - 8–128 characters
//   - at least one letter (any case: no upper/lower distinction)
//   - at least one number
//   - at least one special (non-alphanumeric) character
// Shared by the mobile app and apps/api. Keep `evaluatePassword`,
// `PASSWORD_RULES`, and `passwordSchema` in sync if you change the rules.
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 128;

const PASSWORD_LETTER_REGEX = /[A-Za-z]/;
const PASSWORD_NUMBER_REGEX = /[0-9]/;
const PASSWORD_SPECIAL_REGEX = /[^A-Za-z0-9]/;

export const PASSWORD_REQUIREMENTS_MESSAGE =
  "Password must be 8–128 characters and include at least one letter, one number, and one special character";

export interface PasswordRuleEvaluation {
  minLength: boolean;
  hasLetter: boolean;
  hasNumber: boolean;
  hasSpecial: boolean;
  valid: boolean;
}

/**
 * Framework-agnostic, pure-TS evaluation of a password against the complexity
 * policy. Returns per-rule booleans for real-time UI (issue #25) plus an
 * overall `valid` flag. No React, no zod.
 */
export function evaluatePassword(pw: string): PasswordRuleEvaluation {
  const value = pw ?? "";
  const minLength =
    value.length >= PASSWORD_MIN_LENGTH && value.length <= PASSWORD_MAX_LENGTH;
  const hasLetter = PASSWORD_LETTER_REGEX.test(value);
  const hasNumber = PASSWORD_NUMBER_REGEX.test(value);
  const hasSpecial = PASSWORD_SPECIAL_REGEX.test(value);
  return {
    minLength,
    hasLetter,
    hasNumber,
    hasSpecial,
    valid: minLength && hasLetter && hasNumber && hasSpecial,
  };
}

export type PasswordRuleKey =
  | "minLength"
  | "hasLetter"
  | "hasNumber"
  | "hasSpecial";

export interface PasswordRule {
  key: PasswordRuleKey;
  /** Short label for checklist UIs. */
  label: string;
  /** Sentence-form requirement message. */
  message: string;
  /** Pure predicate used by `evaluatePassword`. */
  test: (pw: string) => boolean;
}

/**
 * Stable, ordered list of password rules with default English copy. Consumers
 * (e.g. the issue #25 real-time checklist) can map over this and pair each rule
 * with the matching boolean from `evaluatePassword`.
 */
export const PASSWORD_RULES: readonly PasswordRule[] = [
  {
    key: "minLength",
    label: "8–128 characters",
    message: "Password must be 8–128 characters",
    test: (pw) =>
      (pw ?? "").length >= PASSWORD_MIN_LENGTH &&
      (pw ?? "").length <= PASSWORD_MAX_LENGTH,
  },
  {
    key: "hasLetter",
    label: "At least one letter",
    message: "Password must include at least one letter",
    test: (pw) => PASSWORD_LETTER_REGEX.test(pw ?? ""),
  },
  {
    key: "hasNumber",
    label: "At least one number",
    message: "Password must include at least one number",
    test: (pw) => PASSWORD_NUMBER_REGEX.test(pw ?? ""),
  },
  {
    key: "hasSpecial",
    label: "At least one special character",
    message: "Password must include at least one special character",
    test: (pw) => PASSWORD_SPECIAL_REGEX.test(pw ?? ""),
  },
] as const;

export const passwordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, "Password must be at least 8 characters")
  .max(PASSWORD_MAX_LENGTH, "Password must be at most 128 characters")
  .refine((v) => PASSWORD_LETTER_REGEX.test(v), {
    message: "Password must include at least one letter",
  })
  .refine((v) => PASSWORD_NUMBER_REGEX.test(v), {
    message: "Password must include at least one number",
  })
  .refine((v) => PASSWORD_SPECIAL_REGEX.test(v), {
    message: "Password must include at least one special character",
  });

export const usernameSchema = z
  .string()
  .min(3, "Username must be at least 3 characters")
  .max(30, "Username must be at most 30 characters")
  .regex(
    /^[a-zA-Z0-9_-]+$/,
    "Username can only contain letters, numbers, hyphens, and underscores",
  );

export const displayNameSchema = z
  .string()
  .trim()
  .min(1, "Name is required")
  .max(100, "Name must be at most 100 characters");

// E.164-compatible phone format: optional leading `+`, 7–15 digits total.
// Use `normalizePhone` to strip user-entered spaces, dashes, and parentheses
// before matching.
export const PHONE_REGEX = /^\+?[0-9]{7,15}$/;
export const PHONE_INVALID_MESSAGE =
  "Phone number must be 7–15 digits, optional leading +";

export function normalizePhone(input: string): string {
  return input.replace(/[\s\-()]/g, "");
}

export function isValidPhone(input: string): boolean {
  return PHONE_REGEX.test(normalizePhone(input));
}

/**
 * Country-specific phone validation (issue #31). Unlike `isValidPhone`, which
 * only checks a loose 7–15 digit shape, this validates the number against the
 * numbering plan of the SELECTED country via libphonenumber-js: so an
 * over-/under-length number (e.g. `+962 79155876230` while Jordan is selected)
 * is rejected.
 *
 * @param rawPhone        The full phone as entered, e.g. `"+962 791234567"`.
 *                        Spaces/dashes/parens are tolerated.
 * @param isoCountryCode  ISO 3166-1 alpha-2 of the selected country, e.g. `"JO"`.
 *                        When omitted, the embedded `+` country code (if any) is
 *                        used; without either, returns `false`.
 */
export function isValidPhoneForCountry(
  rawPhone: string,
  isoCountryCode?: string | null,
): boolean {
  const value = (rawPhone ?? "").trim();
  if (!value) return false;
  const country = isoCountryCode
    ? (isoCountryCode.toUpperCase() as PhoneCountryCode)
    : undefined;
  try {
    return country
      ? isValidPhoneNumber(value, country)
      : isValidPhoneNumber(value);
  } catch {
    return false;
  }
}

export const phoneSchema = z
  .string()
  .trim()
  .transform(normalizePhone)
  .refine((v) => PHONE_REGEX.test(v), PHONE_INVALID_MESSAGE);

export const optionalPhoneSchema = z
  .string()
  .trim()
  .optional()
  .transform((v) => (v ? normalizePhone(v) : v))
  .refine((v) => !v || PHONE_REGEX.test(v), PHONE_INVALID_MESSAGE);

export const userRoleSchema = z.enum(["seeker", "employer"]);

export const paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});

export const jobTypeSchema = z.enum([
  "full-time",
  "part-time",
  "contract",
  "internship",
  "remote",
]);

export const experienceLevelSchema = z.enum([
  "entry",
  "mid",
  "senior",
  "lead",
  "executive",
]);

export const salaryRangeSchema = z
  .object({
    min: z.number().min(0),
    max: z.number().min(0),
    currency: z.string().length(3),
    period: z.enum(["monthly", "yearly"]),
  })
  .refine((data) => data.max >= data.min, {
    message: "Max salary must be greater than or equal to min salary",
  });

export function formatCurrency(
  amount: number,
  currency: string = "AED",
  locale: string = "en-AE",
): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function formatDate(
  date: Date | string,
  locale: string = "en-AE",
  options?: Intl.DateTimeFormatOptions,
): string {
  const normalizedDate = typeof date === "string" ? new Date(date) : date;
  return normalizedDate.toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    ...options,
  });
}

export function formatRelativeTime(date: Date | string): string {
  const normalizedDate = typeof date === "string" ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - normalizedDate.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 30) return formatDate(normalizedDate);
  if (diffDays > 0) return `${diffDays}d ago`;
  if (diffHours > 0) return `${diffHours}h ago`;
  if (diffMinutes > 0) return `${diffMinutes}m ago`;
  return "just now";
}

export const APP_NAME = "Tabbio";
export const APP_TAGLINE = "Your digital CV.";
export const APP_SCHEME = "tabbio";

export const API_VERSION = "v1";

export const PAGINATION_DEFAULTS = {
  page: 1,
  limit: 20,
  maxLimit: 100,
} as const;

export const FILE_UPLOAD = {
  maxSizeMB: 10,
  allowedImageTypes: ["image/jpeg", "image/png", "image/webp"],
  allowedDocTypes: ["application/pdf"],
  presignedUrlExpirySeconds: 3600,
} as const;

export const AGENT_MODES = ["seeker", "employer"] as const;

// Minimum non-whitespace length for a job-listing description. Enforced at
// creation/update on the API (after sanitization) and mirrored client-side.
export const MIN_JOB_DESCRIPTION_LENGTH = 40;

/**
 * The agent tools that always wait for the user's approval before they run,
 * by tool id. This list is the ONLY place that policy lives: `createAuditedTool`
 * (apps/api/src/mastra/tools/shared.ts) reads it, so in Agent Chat Mastra
 * suspends the call with an approval card, and a direct MCP or CLI call
 * returns `approvalRequired` until the user approves it in the app.
 *
 * Keep a tool here when it sends something to another person, publishes or
 * widens who can see something, spends credits on the user's behalf from a
 * plan they have not reviewed, or deletes. Narrowing an audience (unpublish,
 * a revoke) may stay ungated. apps/api/src/mastra/tools/approval-policy.spec.ts
 * fails when an id here names no real tool.
 */
export const CONFIRMATION_REQUIRED_TOOLS = [
  // Applications and hiring
  "application.submit",
  "application.updateStatus",
  "application.withdraw",
  "catalog.applySubmit",
  "candidate.shortlist",
  "job.publish",
  "job.close",
  "companyPage.blocksUpdate",
  "companyPage.socialsUpdate",
  // Messages and people
  "message.send",
  "message.scheduleInterview",
  "conversation.send",
  "conversation.delete",
  "messaging.preferencesUpdate",
  "messaging.userBlock",
  "messaging.userUnblock",
  "messaging.userReport",
  "connector.sendEmail",
  "connector.postSlackMessage",
  "connector.triggerZapier",
  "connector.triggerMake",
  // Profile, privacy and the public profile
  "profile.privateInfoUpdate",
  "profile.privacyUpdate",
  "publicProfile.blocksUpdate",
  "publicProfile.socialsUpdate",
  "publicProfile.contactsUpdate",
  "publicProfile.designUpdate",
  "publicProfile.settingsUpdate",
  "careerHighlight.delete",
  "experience.delete",
  "education.delete",
  "certification.delete",
  "project.delete",
  "serviceOffer.delete",
  // CVs: publication, sharing, collaboration, deletion
  "cv.publish",
  "cv.delete",
  "cv.shareLinkCreate",
  "cv.shareLinkUpdate",
  "cv.shareLinkRevoke",
  "cv.accessGrantCreate",
  "cv.accessGrantUpdate",
  "cv.accessGrantRevoke",
  "cv.sharedProfileUpdate",
  "cv.sharedSectionUpsert",
  "cv.sharedSectionDelete",
  "cv.sharedStructuredUpdate",
  "cv.handoffSend",
  "cv.handoffCancel",
  // Application Studio
  "studio.artifactGenerate",
  "studio.artifactRegenerate",
  "studio.artifactDelete",
  "studio.applySubmit",
  // Skills, Tasks, memory, notifications
  "skills.publish",
  "nova.activate",
  "nova.runNow",
  "memory.forget",
  "memory.nodeDelete",
  "memory.accessRevoke",
  "notification.preferencesUpdate",
  "notification.delete",
] as const;

/**
 * Tools that wait for approval only on a direct MCP or CLI call. In Agent Chat
 * the composer controls already decide when they run (and `publishArtifact`
 * opens the Publish sheet instead of publishing), so Mastra never suspends
 * them there. Read by `createAuditedTool` with the list above.
 */
export const MCP_CONFIRMATION_REQUIRED_TOOLS = [
  "artifact.generateImage",
  "artifact.createDocument",
  "artifact.createPage",
  "artifact.updatePage",
  "artifact.updateDocument",
  "artifact.editImage",
  "publishArtifact",
] as const;

export type ToolApprovalPolicy = "always" | "mcp" | "none";

/** When an agent tool waits for the user's approval (see the two lists above). */
export function getToolApprovalPolicy(toolId: string): ToolApprovalPolicy {
  if ((CONFIRMATION_REQUIRED_TOOLS as readonly string[]).includes(toolId)) {
    return "always";
  }
  if ((MCP_CONFIRMATION_REQUIRED_TOOLS as readonly string[]).includes(toolId)) {
    return "mcp";
  }
  return "none";
}

// Agent tool ids that must never be invokable through Agent Chat or a direct
// MCP tool call (issue #276). The tools stay defined in the codebase and the
// normal non-agent REST creation flows are unaffected, but the assistant is
// blocked from discovering or executing them. Enforced server-side in
// `createAuditedTool` (apps/api/src/mastra/tools/shared.ts).
export const AGENT_BLOCKED_CREATION_TOOLS = [
  "company.create",
  "job.draft",
] as const;

export const EMPLOYER_OUTREACH_MIN_LENGTH = 30;
export const EMPLOYER_OUTREACH_MAX_LENGTH = 5000;
export const EMPLOYER_OUTREACH_DRAFT_INSTRUCTIONS_MAX_LENGTH = 500;
export const EMPLOYER_OUTREACH_PLACEHOLDER_REGEX = /\{\{\s*[^}]+\s*\}\}/;

export type EmployerOutreachValidationIssue =
  | "TOO_SHORT"
  | "TOO_LONG"
  | "CONTAINS_PLACEHOLDER";

export function validateEmployerOutreachMessage(
  message: string,
): { ok: true } | { ok: false; issue: EmployerOutreachValidationIssue } {
  const trimmed = message.trim();
  if (trimmed.length < EMPLOYER_OUTREACH_MIN_LENGTH) {
    return { ok: false, issue: "TOO_SHORT" };
  }
  if (trimmed.length > EMPLOYER_OUTREACH_MAX_LENGTH) {
    return { ok: false, issue: "TOO_LONG" };
  }
  if (EMPLOYER_OUTREACH_PLACEHOLDER_REGEX.test(trimmed)) {
    return { ok: false, issue: "CONTAINS_PLACEHOLDER" };
  }
  return { ok: true };
}
